import React, { useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { dateFormat, prettifyDate } from 'lib/dateUtilities';
import { AuthContext } from 'components/user/AuthContext';
import { ContextMenu } from 'components/element/ContextMenu';
import { NavigationUtils } from 'lib/navigationUtilities';
import Dialog from 'components/element/Dialog';
import Avatar from 'components/user/Avatar';
import Like from './actions/Like';
import Reply from './actions/Reply';
import TwutForm from './TwutForm';

const Twut = ({ twut, onUpdate, onDelete, onReply, isUpdating, className = '' }) => {
  const { user: authenticatedUser } = useContext(AuthContext);
  const isAuthor = authenticatedUser !== null && authenticatedUser.username === twut.user.username;
  const [isEditing, setIsEditing] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { locationPathName, navigateToThread } = NavigationUtils();

  const isThreadView = locationPathName.includes(`/twuts/${twut.id}`);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSaveEdit = (updatedContent) => {
    onUpdate(twut.id, { content: updatedContent.content });
    setIsEditing(false);
  };

  const handleDelete = () => {
    handleCancelEdit();
    setShowDeleteDialog(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleReply = () => {
    setIsReplying(true);
  };

  const handleSubmitReply = (replyContent) => {
    onReply(twut.id, replyContent);
    setIsReplying(false);
  };

  const handleCancelReply = () => {
    setIsReplying(false);
  };

  const handleContentClick = () => {
    if (!isThreadView) {
      navigateToThread(twut.id);
    }
  };

  const deleteMessages = [
    "Are you sure you want to delete this twut?",
    "This action cannot be undone."
  ];

  const deleteButtons = [
    {
      label: 'Delete',
      className: 'text-white bg-red-mid hover:bg-red-more',
      onClick: () => onDelete?.(twut.id)
    },
    {
      label: 'Cancel',
      className: 'text-white bg-gray-mid hover:bg-gray-more',
      onClick: () => setShowDeleteDialog(false)
    }
  ];

  return (
    <div className={`twut bg-gray-weakest border border-gray-less p-4 mb-2 rounded-lg flex items-start space-x-2 ${className}`}>
      <Link to={`/${twut.user.username}`}>
        <Avatar src={twut.user.avatar} alt={twut.user.name} className="w-10 h-10 mt-2" />
      </Link>
      <div className="content flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-1 text-gray-more min-w-0">
            <Link to={`/${twut.user.username}`} className="font-bold text-gray-strongest hover:underline truncate min-w-0 max-w-[150px] md:max-w-[400px] flex-shrink-0">
              {twut.user.name}
            </Link>
            <Link to={`/${twut.user.username}`} className="hover:underline truncate flex-shrink ml-1">
              @{twut.user.username}
            </Link>
            <span> · </span>
            <span title={prettifyDate(twut.created_on)} className="shrink-0">
              {dateFormat(twut.created_on)}
            </span>
          </div>
          {isAuthor && !isUpdating && (
            <>
              <ContextMenu
                trigger={
                  <button className="text-gray-more hover:text-gray-strong p-1 rounded-full hover:bg-gray-weakest transition-colors">
                    <i className="fas fa-ellipsis-h w-5 h-5"></i>
                  </button>
                }
                align="right"
              >
                <ContextMenu.Item
                  icon={<i className="fas fa-edit w-4"></i>}
                  onClick={handleEdit}
                >
                  Edit Twut
                </ContextMenu.Item>
                <ContextMenu.Separator />
                <ContextMenu.Item
                  icon={<i className="fas fa-trash-alt w-4"></i>}
                  className="text-red-more"
                  onClick={handleDelete}
                >
                  Delete Twut
                </ContextMenu.Item>
              </ContextMenu>

              <Dialog
                isOpen={showDeleteDialog}
                onClose={() => setShowDeleteDialog(false)}
                title="Delete Twut?"
                messages={deleteMessages}
                buttons={deleteButtons}
              />
            </>
          )}
        </div>
        {isEditing ? (
          <TwutForm initialContent={twut.content} onSubmit={handleSaveEdit} onCancel={handleCancelEdit} isProcessing={isUpdating} />
        ) : (
          <>
            <div
              className={`mt-1 text-gray-strong ${!isThreadView && 'cursor-pointer'}`}
              onClick={!isThreadView ? handleContentClick : undefined}
            >
              {twut.content}
              {twut.created_on !== twut.updated_on && (
                <p className="mt-1 text-sm text-gray-mid italic" title={prettifyDate(twut.updated_on)}>Edited: {dateFormat(twut.updated_on, " ago")}</p>
              )}
            </div>
            <div className="mt-4 grid grid-cols-3 text-left max-w-80">
              <Reply repliesCount={twut.replies_count} onReply={handleReply} />
              <Like twutId={twut.id} currentUserLiked={twut.currentUserLiked} initialLikesCount={twut.likes} />
            </div>
            {isReplying && (
              <div className="mt-4 pl-2">
                <TwutForm onSubmit={handleSubmitReply} onCancel={handleCancelReply} isProcessing={isUpdating} placeholder={`Reply to @${twut.user.username}`} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Twut;
